import { useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog from "@/v2-ui/dialog"
import Spinner from "@/v2-ui/spinner"
import Button, {
  type ButtonProps,
} from "@/v2-backoffice-client/components/button"
import TenantPrivacyHistoryEvent from "@/v2-backoffice-client/tenant/privacy/TenantPrivacyHistoryEvent"
import { useTenantPrivacyCatalogQuery } from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.catalog.query"
import { useTenantPrivacyHistoryQuery } from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.history.query"

export type TenantPrivacyHistoryButtonProps = Omit<ButtonProps, "onClick"> & {
  tenantId: number
}

// The audit trail behind the team's personal-data policy, opened from the
// editor's status row: every write, newest first, with who made it, why,
// and what moved per source. The query runs only while the dialog is open
// and goes to the network on every opening (the policy the operator just
// saved must be the first entry they see), and the catalogue it labels
// fields and codes with is the one the editor already holds in the cache.
function TenantPrivacyHistoryButton(props: TenantPrivacyHistoryButtonProps) {
  const { tenantId, children, ...buttonProps } = props
  const [isOpen, setIsOpen] = useState(false)

  const catalog = useTenantPrivacyCatalogQuery({ skip: !isOpen })
  const history = useTenantPrivacyHistoryQuery({
    variables: { tenantId },
    skip: !isOpen,
    fetchPolicy: "cache-and-network",
  })

  const changes = history.data?.getTenantPrivateDataVisibilityHistory ?? []
  const fields = catalog.data?.getPrivateDataFields ?? []
  const visibilityTypes = catalog.data?.getPrivateDataVisibilityTypes ?? []
  const isLoading = history.loading || catalog.loading
  const error = history.error ?? catalog.error

  return (
    <>
      <Button
        type="button"
        size="xs"
        variant="ghost"
        iconSrc="clock"
        data-feature-code="UI_BackofficeTenantPrivacy_History_Open"
        onClick={() => {
          setIsOpen(true)
        }}
        {...buttonProps}
      >
        {children ?? "History"}
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <Dialog.Content align="top" className="max-w-2xl">
          <Dialog.Header>
            <Dialog.Title>Personal-data policy history</Dialog.Title>
            <Dialog.Description>
              Every change to what this team&apos;s members may see of personal
              data, newest first — who made it, why, and what it opened or
              closed per source.
            </Dialog.Description>
          </Dialog.Header>

          {/* Partial data over a blanket error: what did load still renders
              and the failure is stated beside it (errorPolicy is "all"). */}
          <ErrorMessage className="my-4 text-center" error={error} />

          {isLoading && changes.length === 0 && (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          )}

          {/* Newest write open, the rest as one-line summaries: the trail is
              scanned by date and actor, and expanded only where it matters. */}
          <ol className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
            {changes.map((change, index) => (
              <TenantPrivacyHistoryEvent
                key={change.ChangedAt}
                change={change}
                fields={fields}
                visibilityTypes={visibilityTypes}
                defaultOpen={index === 0}
              />
            ))}
          </ol>

          {!isLoading && changes.length === 0 && !error && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No changes recorded — this team&apos;s policy has never been set.
            </p>
          )}
        </Dialog.Content>
      </Dialog>
    </>
  )
}

export default TenantPrivacyHistoryButton
